import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle } from 'lucide-react';
import apiService from '../services/api';
import { sendOTPEmail } from '../services/emailService';
import '../styles/auth.css';

const ForgotPassword = () => {
  const [step, setStep] = useState(1); // 1: Email, 2: OTP
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const navigate = useNavigate();
  const otpInputRef = useRef(null);

  // Countdown timer for resend OTP
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Auto-focus on OTP input when step changes
  useEffect(() => {
    if (step === 2 && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [step]);

  // Handle email submission and OTP generation
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      // Call API to generate OTP
      const response = await apiService.forgotPassword(email);

      if (response.success && response.data) {
        const { otp: generatedOtp } = response.data;

        // Send OTP via Email.js
        const emailResult = await sendOTPEmail(email, generatedOtp);

        if (emailResult.success) {
          setSuccess('OTP sent successfully to your email!');
          setCountdown(60); // 60 seconds countdown for resend
          
          // Store email in localStorage for reset password page
          localStorage.setItem('reset_email', email);

          // Move to step 2 after 1.5 seconds
          setTimeout(() => {
            setStep(2);
            setSuccess('');
          }, 1500);
        } else {
          setError(emailResult.message || 'Failed to send OTP email. Please try again.');
        }
      } else {
        setError(response.message || 'Failed to generate OTP. Please try again.');
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP verification
  const handleOtpVerify = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validate OTP
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      setLoading(false);
      return;
    }

    try {
      // Call API to verify OTP
      const response = await apiService.verifyOTP(email, otp);

      if (response.success) {
        setSuccess('OTP verified successfully!');
        
        // Store OTP in localStorage for reset password page
        localStorage.setItem('reset_otp', otp);

        // Navigate to reset password page after 1 second
        setTimeout(() => {
          navigate('/reset-password');
        }, 1000);
      } else {
        setError(response.message || 'Invalid or expired OTP. Please try again.');
      }
    } catch (err) {
      console.error('OTP verification error:', err);
      setError(err.message || 'Failed to verify OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle resend OTP
  const handleResendOtp = async () => {
    if (countdown > 0) return;

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Call API to generate new OTP
      const response = await apiService.forgotPassword(email);

      if (response.success && response.data) {
        const { otp: generatedOtp } = response.data;

        // Send OTP via Email.js
        const emailResult = await sendOTPEmail(email, generatedOtp);

        if (emailResult.success) {
          setSuccess('New OTP sent successfully!');
          setCountdown(60);
          setOtp(''); // Clear previous OTP input
          
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError(emailResult.message || 'Failed to send OTP email. Please try again.');
        }
      } else {
        setError(response.message || 'Failed to generate OTP. Please try again.');
      }
    } catch (err) {
      console.error('Resend OTP error:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP input (numbers only, max 6 digits)
  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    if (value.length <= 6) {
      setOtp(value);
    }
  };

  // Handle back to email step
  const handleBackToEmail = () => {
    setStep(1);
    setOtp('');
    setError('');
    setSuccess('');
  };

  return (
    <div className="auth-container">
      <div className="auth-background">
        <div className="auth-shape shape-1"></div>
        <div className="auth-shape shape-2"></div>
        <div className="auth-shape shape-3"></div>
      </div>

      <div className="auth-content">
        <div className="auth-card" style={{ padding: '2rem', maxWidth: '420px' }}>
          <div className="auth-header" style={{ marginBottom: '1.25rem' }}>
            <h1 className="auth-title" style={{ fontSize: '1.75rem', margin: 0 }}>
              {step === 1 ? 'Forgot Password?' : 'Verify OTP'}
            </h1>
            <p className="auth-subtitle" style={{ fontSize: '0.875rem', marginTop: '0.25rem', color: '#94a3b8' }}>
              {step === 1
                ? "Enter your email address and we'll send you an OTP to reset your password"
                : `We've sent a 6-digit OTP to ${email}`}
            </p>
          </div>

          {error && (
            <div className="auth-error" style={{ marginBottom: '1rem', padding: '0.75rem 1rem' }}>
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="auth-success" style={{ marginBottom: '1rem', padding: '0.75rem 1rem' }}>
              <CheckCircle size={18} />
              <span>{success}</span>
            </div>
          )}

          {/* Step 1: Email Input */}
          {step === 1 && (
            <form onSubmit={handleEmailSubmit} className="auth-form">
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <input
                  type="email"
                  id="email"
                  className="form-input"
                  placeholder="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  autoComplete="email"
                  autoFocus
                />
              </div>

              <button
                type="submit"
                className="auth-button"
                disabled={loading}
                style={{ padding: '0.75rem 1.5rem', fontSize: '0.95rem' }}
              >
                {loading ? (
                  <>
                    <div className="button-spinner"></div>
                    Sending OTP...
                  </>
                ) : (
                  "Send OTP"
                )}
              </button>
            </form>
          )}

          {/* Step 2: OTP Verification */}
          {step === 2 && (
            <form onSubmit={handleOtpVerify} className="auth-form">
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <input
                  type="text"
                  id="otp"
                  className="form-input otp-input"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={handleOtpChange}
                  disabled={loading}
                  maxLength={6}
                  ref={otpInputRef}
                  autoComplete="off"
                  style={{ textAlign: 'center', letterSpacing: '0.25rem', fontSize: '1.25rem' }}
                />
              </div>

              <button
                type="submit"
                className="auth-button"
                disabled={loading || otp.length !== 6}
                style={{ padding: '0.75rem 1.5rem', fontSize: '0.95rem' }}
              >
                {loading ? (
                  <>
                    <div className="button-spinner"></div>
                    Verifying...
                  </>
                ) : (
                  "Verify OTP"
                )}
              </button>

              <div className="resend-otp" style={{ margin: '1rem 0 0.5rem 0', textAlign: 'center' }}>
                <p style={{ fontSize: '0.85rem', margin: 0 }}>
                  Didn't receive the code?{' '}
                  <button
                    type="button"
                    className={`resend-link ${countdown > 0 ? 'disabled' : ''}`}
                    onClick={handleResendOtp}
                    disabled={countdown > 0 || loading}
                    style={{ background: 'none', border: 'none', color: '#38bdf8', cursor: 'pointer', padding: 0 }}
                  >
                    {countdown > 0 ? `Resend in ${countdown}s` : 'Resend OTP'}
                  </button>
                </p>
              </div>

              <button
                type="button"
                className="back-button"
                onClick={handleBackToEmail}
                disabled={loading}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  marginTop: '0.5rem',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.25rem'
                }}
              >
                ← Back to Email
              </button>
            </form>
          )}

          <div className="auth-divider" style={{ margin: '1rem 0' }}>
            <span>or</span>
          </div>

          <div className="auth-footer" style={{ marginTop: '0.25rem' }}>
            <p style={{ fontSize: '0.85rem', margin: 0 }}>
              Remember your password?{' '}
              <Link to="/login" className="auth-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
